import React, { useState } from "react";
import { isRepeating } from "../utils/habits.js";
import { countSportProgress } from "../utils/sport.js";

/** Six-dot grip shown in edit mode (mirrors HabitCard). */
function Grip() {
  return (
    <svg
      width="12"
      height="18"
      viewBox="0 0 12 18"
      aria-hidden="true"
      className="text-ink-soft"
    >
      {[3, 9, 15].map((y) =>
        [3, 9].map((x) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="1.4" fill="currentColor" />
        )),
      )}
    </svg>
  );
}

/**
 * A sport occurrence in the day sheet.
 *
 * Sits in the same list as the habits, always last, but circled with a dashed
 * outline so it reads as a different kind of entry. Struck through when done
 * like any habit — except tapping it opens the session window instead of
 * toggling, because the tick is earned by filling in the numbers.
 *
 * @param {{habit: object, date: string, baseDate: string}} occurrence
 * @param {Record<string, string>} values current log, for the progress hint
 * @param {boolean} done every numeric field filled in
 * @param {boolean} editMode
 * @param {boolean} isDragSource
 * @param {() => void} onOpen
 * @param {(scope: 'day'|'series') => void} onDelete
 * @param {(event: React.PointerEvent) => void} onDragStart
 */
export default function SportCard({
  occurrence,
  values,
  done,
  editMode,
  isDragSource,
  onOpen,
  onDelete,
  onDragStart,
}) {
  const { habit } = occurrence;
  const repeats = isRepeating(habit);
  const [confirming, setConfirming] = useState(false);
  const { filled, total } = countSportProgress(habit.sport, values);

  const handleDelete = () => {
    if (repeats) setConfirming(true);
    else onDelete("day");
  };

  return (
    <div
      // The dashed ring is what sets a sport apart from a plain habit line.
      className={`my-0.5 flex items-center gap-2 py-1
                  transition-opacity ${done ? "border-done/60" : "border-line"} ${
                    isDragSource ? "opacity-30" : "opacity-100"
                  } ${
                    editMode
                      ? "cursor-grab select-none bg-paper-deep/50 no-touch-scroll active:cursor-grabbing"
                      : ""
                  }`}
      onPointerDown={editMode ? onDragStart : undefined}
    >
      {editMode && <Grip />}

      <button
        type="button"
        onClick={editMode ? undefined : onOpen}
        aria-label={editMode ? undefined : `Open ${habit.name} session`}
        className="flex min-w-0 flex-1 items-baseline gap-1.5 text-left"
        tabIndex={editMode ? -1 : 0}
      >
        <span
          className={`min-w-0 truncate font-hand text-lg leading-tight transition-colors ${
            done
              ? "text-ink-soft line-through decoration-accent decoration-2"
              : ""
          }`}
        >
          {habit.name}
        </span>

        {/* Partial progress is worth seeing without opening the window */}
        {!done && (
          <span className="shrink-0 text-[10px] uppercase tracking-widest text-ink-soft">
            {filled}/{total}
          </span>
        )}
      </button>

      {editMode && !confirming && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={handleDelete}
          aria-label={`Delete ${habit.name}`}
          className="shrink-0 rounded-pill px-1.5 text-xs text-accent hover:bg-accent-soft"
        >
          ✕
        </button>
      )}

      {editMode && confirming && (
        // Two-step delete for repeating sports, same as habits.
        <div
          className="flex shrink-0 gap-1"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => onDelete("day")}
            className="rounded-pill border border-line px-1.5 py-0.5 text-[10px] uppercase"
          >
            day
          </button>
          <button
            type="button"
            onClick={() => onDelete("series")}
            className="rounded-pill border border-accent bg-accent px-1.5 py-0.5 text-[10px] uppercase text-white"
          >
            all
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            aria-label="Cancel delete"
            className="px-1 text-[10px] text-ink-soft"
          >
            ↺
          </button>
        </div>
      )}
    </div>
  );
}
